import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import learningProfileManPNG from '~/assets/images/anatomy/learning-profile-man.png'
import learningProfileManWEBP from '~/assets/images/anatomy/learning-profile-man.webp'
import learningProfileWomanPNG from '~/assets/images/anatomy/learning-profile-woman.png'
import learningProfileWomanWEBP from '~/assets/images/anatomy/learning-profile-woman.webp'
import { PageId } from '~/shared/constants/pagesConstants'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { selectAnswers } from '~/shared/store/common/selects'
import { TPageProps } from '~/shared/types/commonTypes'

import { PROFILE_ACCENT_COLOR, Styles as S } from './styles'

const FOCUS_AREAS_I18N_MAP: Record<string, string> = {
  bonesAndMuscles:
    'onboarding.learningProfileAnatomy.focusAreas.bonesAndMuscles',
  brainAndNerves: 'onboarding.learningProfileAnatomy.focusAreas.brainAndNerves',
  digestionAndGutHealth:
    'onboarding.learningProfileAnatomy.focusAreas.digestionAndGutHealth',
  hormonesAndEnergy:
    'onboarding.learningProfileAnatomy.focusAreas.hormonesAndEnergy',
  pelvicFloorHealth:
    'onboarding.learningProfileAnatomy.focusAreas.pelvicFloorHealth',
}

const DAILY_PACE_I18N_MAP: Record<string, string> = {
  casual: 'onboarding.learningProfileAnatomy.dailyPace.casual',
  regular: 'onboarding.learningProfileAnatomy.dailyPace.regular',
  serious: 'onboarding.learningProfileAnatomy.dailyPace.serious',
  determined: 'onboarding.learningProfileAnatomy.dailyPace.determined',
}

const MAX_DISTRACTIONS = 3

type TProfileItem = {
  icon?: string
  imageSrc?: string
  labelKey: string
  valueKey?: string
  dynamicValue?: string
}

const LearningProfileAnatomy = ({ nextPagePath, pageId }: TPageProps) => {
  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'Your learning profile',
  })

  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const imageLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (imageLoadTimerRef.current) clearTimeout(imageLoadTimerRef.current)
    }
  }, [])

  const { t } = useTranslation('anatomy')
  const answers = useSelector(selectAnswers)

  const curiosityAnswers = answers?.[PageId.ANATOMY_CURIOSITY_1]
  const focusAreasValue = (() => {
    if (!curiosityAnswers) {
      return t('onboarding.learningProfileAnatomy.focusAreas.default')
    }
    const selected = Array.isArray(curiosityAnswers)
      ? curiosityAnswers
      : [curiosityAnswers]
    const mapped = selected
      .filter((key) => key !== 'other')
      .map((key) => FOCUS_AREAS_I18N_MAP[key])
      .filter(Boolean)
      .map((i18nKey) => t(i18nKey))
    return mapped.length
      ? mapped.join(', ')
      : t('onboarding.learningProfileAnatomy.focusAreas.default')
  })()

  const dailyPaceAnswer = answers?.[PageId.DAILY_GOAL_ANATOMY_1]
  const dailyPaceKey = DAILY_PACE_I18N_MAP[String(dailyPaceAnswer)]
  const dailyPaceValue = dailyPaceKey ? t(dailyPaceKey) : ''

  const learningStyleAnswers = answers?.[PageId.KIND_OF_LEARNING_ANATOMY_1]
  const learningStyleValue = (() => {
    if (!learningStyleAnswers) return ''
    const selected = Array.isArray(learningStyleAnswers)
      ? learningStyleAnswers
      : [learningStyleAnswers]
    return selected
      .map((key) =>
        t(`onboarding.kindOfLearningAnatomy.options.${key}`).replace(
          /^\S+\s/,
          '',
        ),
      )
      .filter(Boolean)
      .join(', ')
  })()

  const genderAnswer = String(answers?.['gender-1'] ?? '')
  const isMale = genderAnswer === 'male'
  const profileImageWEBP = isMale
    ? learningProfileManWEBP
    : learningProfileWomanWEBP
  const profileImagePNG = isMale
    ? learningProfileManPNG
    : learningProfileWomanPNG

  const distractionsAnswers = answers?.[PageId.DISTRACTIONS_ANATOMY_1]
  const distractionsValue = (() => {
    if (!distractionsAnswers) return ''
    const selected = Array.isArray(distractionsAnswers)
      ? distractionsAnswers
      : [distractionsAnswers]
    return selected
      .slice(0, MAX_DISTRACTIONS)
      .map((key) =>
        t(`onboarding.anatomyDistractions.options.${key}`).replace(
          /^\S+\s/,
          '',
        ),
      )
      .filter(Boolean)
      .join(', ')
  })()

  const PROFILE_ITEMS: TProfileItem[] = [
    {
      icon: '⏱️',
      labelKey: 'onboarding.learningProfileAnatomy.dailyPaceLabel',
      dynamicValue: dailyPaceValue,
    },
    {
      icon: '🎯',
      labelKey: 'onboarding.learningProfileAnatomy.focusAreasLabel',
      dynamicValue: focusAreasValue,
    },
    {
      icon: '👁️',
      labelKey: 'onboarding.learningProfileAnatomy.learningStyleLabel',
      dynamicValue: learningStyleValue,
    },
    {
      icon: '🚧️',
      labelKey: 'onboarding.learningProfileAnatomy.distractionsLabel',
      dynamicValue: distractionsValue,
    },
    {
      icon: '🌱',
      labelKey: 'onboarding.learningProfileAnatomy.startingPointLabel',
      valueKey: 'onboarding.learningProfileAnatomy.startingPointValue',
    },
  ]

  return (
    <S.Root>
      <S.StyledContainer>
        <S.Title>
          <Trans
            ns="anatomy"
            i18nKey="onboarding.learningProfileAnatomy.title"
          />
        </S.Title>

        <S.ImageCard>
          <S.ImageWrapper>
            <S.ImageTopGradient />
            <S.ImageHeaderRow>
              <S.AnatomyLabel>
                <Trans
                  ns="anatomy"
                  i18nKey="onboarding.learningProfileAnatomy.masteryLabel"
                />
              </S.AnatomyLabel>
              <S.HighBadge>
                <Trans
                  ns="anatomy"
                  i18nKey="onboarding.learningProfileAnatomy.highBadge"
                />
              </S.HighBadge>
            </S.ImageHeaderRow>

            <picture>
              <source srcSet={profileImageWEBP} type="image/webp" />
              <S.CharacterImage
                src={profileImagePNG}
                alt="Learning profile"
                onLoad={() => {
                  imageLoadTimerRef.current = setTimeout(
                    () => setIsImageLoaded(true),
                    500,
                  )
                }}
              />
            </picture>

            <S.ImageBottomGradient />
            <S.ProgressSection>
              <S.ProgressBar>
                <S.LevelIndicator $isVisible={isImageLoaded}>
                  <S.LevelPill>
                    <Trans
                      ns="anatomy"
                      i18nKey="onboarding.learningProfileAnatomy.yourLevel"
                    />
                  </S.LevelPill>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="26"
                    height="10"
                    viewBox="0 0 26 10"
                    fill="none"
                    style={{ marginTop: '-1px' }}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.300781 0C1.99516 0 3.61055 0.716408 4.74797 1.97228L9.9594 7.72645C11.5472 9.47958 14.3012 9.47958 15.889 7.72645L21.1004 1.97228C22.2378 0.716408 23.8532 0 25.5476 0H26L0 0H0.300781Z"
                      fill={PROFILE_ACCENT_COLOR}
                    />
                  </svg>
                  <S.LevelDot />
                </S.LevelIndicator>
              </S.ProgressBar>
            </S.ProgressSection>
          </S.ImageWrapper>
        </S.ImageCard>

        <S.ProfileCard>
          {PROFILE_ITEMS.map(
            ({ icon, imageSrc, labelKey, valueKey, dynamicValue }) => (
              <S.ProfileItem key={labelKey}>
                <S.ProfileIcon>
                  {imageSrc ? (
                    <img src={imageSrc} alt="" width={38} height={38} />
                  ) : (
                    icon
                  )}
                </S.ProfileIcon>
                <S.ProfileTextWrapper>
                  <S.ProfileLabel>
                    <Trans ns="anatomy" i18nKey={labelKey} />
                  </S.ProfileLabel>
                  <S.ProfileValue>
                    {dynamicValue ?? <Trans ns="anatomy" i18nKey={valueKey} />}
                  </S.ProfileValue>
                </S.ProfileTextWrapper>
              </S.ProfileItem>
            ),
          )}
        </S.ProfileCard>

        <S.StyledButton type="button" onClick={() => handleContinue('')}>
          <Trans i18nKey="actions.continue" />
        </S.StyledButton>
      </S.StyledContainer>
    </S.Root>
  )
}

export default LearningProfileAnatomy
